/**
 * Config Registry Page Component
 * Main list view for Config Registry feature
 */

import { useEffect, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Badge,
  useTheme,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/Tune';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGlobalConfig } from 'context/GlobalContext';
import { BglLoading } from 'components/loading';
import {
  BglPrimarySmallButton,
  BglSecondarySmallButton,
} from 'components/button/BglButtons';
import {
  selectConfigRegistryConfigs,
  selectConfigRegistryPagination,
  selectConfigRegistryFilters,
  selectConfigRegistryIsLoading,
  selectConfigRegistryError,
  selectHasActiveFilters,
  setConfigs,
  setPagination,
  setIsLoading,
  setError,
  setUISchema,
  setConfigTypes,
  toggleFilterPanel,
} from '../store/configRegistryUISlice';
import { ConfigRegistryApis } from '../api/configRegistryAPI';
import { ConfigRegistryTable } from './ConfigRegistryTable';
import { ConfigRegistryFiltersDrawer } from './ConfigRegistryFilters';
import { TemplatesTab } from './TemplatesTab';
import { ConfigRegistryItem, UISchemaResponse } from '../types/configRegistryTypes';

export const ConfigRegistryPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rc } = useGlobalConfig();
  const [searchParams] = useSearchParams();

  const configs = useSelector(selectConfigRegistryConfigs);
  const pagination = useSelector(selectConfigRegistryPagination);
  const filters = useSelector(selectConfigRegistryFilters);
  const isLoading = useSelector(selectConfigRegistryIsLoading);
  const error = useSelector(selectConfigRegistryError);
  const hasActiveFilters = useSelector(selectHasActiveFilters);

  const [activeTab, setActiveTab] = useState('configs');

  const canCreate = rc.permissionsManager.canConfigRegistryEdit();
  const canViewTemplates = rc.permissionsManager.canTemplateView();

  // Read tab from URL query parameter on mount
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'templates' && canViewTemplates) {
      setActiveTab('templates');
    } else if (tabParam === 'configs') {
      setActiveTab('configs');
    }
  }, [searchParams, canViewTemplates]);

  // Build tabs dynamically based on permissions
  const tabs = [
    { value: 'configs', label: 'Configurations', disabled: false },
    ...(canViewTemplates ? [{ value: 'templates', label: 'Templates', disabled: false }] : []),
    { value: 'strings', label: 'Strings (Coming Soon)', disabled: true },
  ];

  // Fetch configs with current filters and pagination
  const fetchConfigs = useCallback(async () => {
    dispatch(setIsLoading(true));
    dispatch(setError(undefined));

    try {
      const response = await ConfigRegistryApis.getConfigs(rc, {
        page: pagination.page,
        pageSize: pagination.pageSize,
        configType: filters.configType || undefined,
        verificationStatus: filters.verificationStatus || filters.approvalStatus || undefined,
        expand: 'full',
        // Lookup type filters
        component: filters.component || undefined,
        section: filters.section || undefined,
        classification: filters.classification || undefined,
        fileType: filters.fileType || undefined,
        search: filters.search || undefined,
        restricted: filters.restricted || undefined,
      });

      if (response.error) {
        dispatch(setError(response.error.message || 'Failed to fetch configs'));
      } else if (response.data) {
        // API returns { data: [...], meta: { page, pageSize, pageCount, total, hasMore } }
        dispatch(setConfigs(response.data));

        // Handle pagination - API now returns total and pageCount directly
        const meta = response.meta as {
          page?: number;
          pageSize?: number;
          pageCount?: number;
          total?: number;
          hasMore?: boolean;
        } | undefined;

        if (meta) {
          dispatch(setPagination({
            page: meta.page || 1,
            pageSize: meta.pageSize || 10,
            pageCount: meta.pageCount || 1,
            total: meta.total || 0,
          }));
        }
      }
    } catch (err: any) {
      dispatch(setError(err.message || 'An error occurred'));
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [
    rc,
    dispatch,
    pagination.page,
    pagination.pageSize,
    filters.configType,
    filters.verificationStatus,
    filters.approvalStatus,
    filters.component,
    filters.section,
    filters.classification,
    filters.fileType,
    filters.search,
    filters.restricted,
  ]);

  // Fetch UI schema and config types on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [schemaRes, typesRes] = await Promise.all([
          ConfigRegistryApis.getUISchema(rc),
          ConfigRegistryApis.getConfigTypes(rc),
        ]);

        if (schemaRes.data) {
          // Normalize the UI schema to have consistent field names
          const schemaData = schemaRes.data as any;
          const rawLookupTypes = schemaData.lookupTypes || schemaData.lookupFields || [];

          const normalizedLookupTypes = rawLookupTypes.map((lt: any) => ({
            ...lt,
            lookups: (lt.lookups || lt.options || []).map((opt: any) => ({
              ...opt,
              documentId: opt.documentId || opt.value,
              displayName: opt.displayName || opt.label || opt.code,
            })),
          }));

          const normalizedSchema: UISchemaResponse = {
            lookupTypes: normalizedLookupTypes,
            hierarchy: schemaData.hierarchy || {},
          };
          dispatch(setUISchema(normalizedSchema));
        }
        if (typesRes.data) {
          // Handle different response structures
          const typesData = typesRes.data as any;
          const types = typesData?.data || typesData || [];
          dispatch(setConfigTypes(Array.isArray(types) ? types : []));
        }
      } catch (err) {
        console.error('Failed to fetch metadata:', err);
      }
    };

    fetchMetadata();
  }, [rc, dispatch]);

  // Fetch configs when filters or pagination change
  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handlePageChange = (page: number, pageSize: number) => {
    dispatch(setPagination({ ...pagination, page, pageSize }));
  };

  const handleRowClick = (config: ConfigRegistryItem) => {
    navigate(`/cms/config-registry/${config.documentId}`);
  };

  const handleEdit = (config: ConfigRegistryItem) => {
    navigate(`/cms/config-registry/${config.documentId}?mode=edit`);
  };

  const handleCreate = () => {
    navigate('/cms/config-registry/new');
  };

  // Loading state
  if (isLoading && configs.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <BglLoading />
      </Box>
    );
  }

  // Error state
  if (error && configs.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
        <Typography variant="h6" color="error">{error}</Typography>
        <BglPrimarySmallButton onClick={fetchConfigs} sx={{ mt: 2 }}>
          Retry
        </BglPrimarySmallButton>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      p: { xs: 2, sm: 3 },
      height: '100%',
      overflow: 'auto',
      bgcolor: 'background.default'
    }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        {/* Row 1: Header - Title/Description on left, Actions on right */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2,
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 }
        }}>
          {/* Left side - Icon, Title, Description */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <TuneIcon sx={{ fontSize: 28, color: 'primary.main', mt: 0.5 }} />
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
                Configuration Registry
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Manage and consult the central registry of all configuration parameters and strings for the platform.
              </Typography>
            </Box>
          </Box>

          {/* Right side - Actions */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
            width: { xs: '100%', md: 'auto' },
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Badge color="primary" variant="dot" invisible={!hasActiveFilters}>
              <BglSecondarySmallButton
                startIcon={<FilterListIcon />}
                onClick={() => dispatch(toggleFilterPanel())}
                fullWidth
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Filters
              </BglSecondarySmallButton>
            </Badge>

            {canCreate && (
              <BglPrimarySmallButton
                startIcon={<AddIcon />}
                onClick={handleCreate}
                fullWidth
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Create New Configuration
              </BglPrimarySmallButton>
            )}
          </Box>
        </Box>

        {/* Row 2: Tabs on left, View Level on right */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                minHeight: 36,
                textTransform: 'none',
                fontWeight: 500,
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                disabled={tab.disabled}
              />
            ))}
          </Tabs>

          {/* View Level - Hidden for now, may revisit later */}
          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">View Level:</Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={viewLevel}
                onChange={(e) => setViewLevel(e.target.value)}
              >
                {VIEW_LEVELS.map((level) => (
                  <MenuItem key={level.value} value={level.value} disabled={level.disabled}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box> */}
        </Box>

        {/* Tab Content */}
        {activeTab === 'configs' && (
          <>
            {/* Table with integrated search via toolbar */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ConfigRegistryTable
                onPageChange={handlePageChange}
                onRowClick={handleRowClick}
                onEdit={handleEdit}
              />
            </Box>
          </>
        )}

        {activeTab === 'templates' && canViewTemplates && (
          <Box sx={{ flex: 1 }}>
            <TemplatesTab />
          </Box>
        )}
      </Paper>

      {/* Filter Drawer */}
      <ConfigRegistryFiltersDrawer />
    </Box>
  );
};

export default ConfigRegistryPage;

